import React, { useEffect, useMemo, useRef, useState } from 'react';

/**
 * AgentSwipeDeck — THE mobile marketplace landing (Nina 08-14).
 *
 * Phones hitting /marketplace meet the agents one full-bleed card at a time:
 * swipe right = capture (the EXISTING anonymous capture mechanic — the parent
 * passes its handleCapture; this component owns zero persistence), swipe left
 * = pass for this deck only, tap = open the agent's page. The classic browse
 * homepage stays one tap away ("Browse all", top right) and the end-of-deck
 * card links back to it — the deck is a front door, never a wall.
 *
 * Rules this obeys:
 *   · Presentation only: no new channel, no new state server-side. A right
 *     swipe calls the same capture path as the grid's 🎯 button.
 *   · Deck order is built ONCE from the first non-empty agents list —
 *     capture bumps refresh the agents array identity, and a rebuild would
 *     reshuffle the deck mid-swipe under the user's thumb.
 *   · Already-captured agents are dealt out of the deck at build time; a
 *     left swipe is session-scoped only (the agent returns next visit).
 *   · Transform-only animation, no blur/shadow animation (perf doctrine),
 *     and prefers-reduced-motion skips the fling entirely.
 *   · The ✖ / 🎯 buttons are the no-gesture path — the deck must be fully
 *     usable without a swipe (accessibility + desktop stragglers).
 */

const SWIPE_THRESHOLD = 90;   // px of drag that commits an action
const FLING_MS = 280;         // fling-off animation time
const TAP_SLOP = 8;           // px of movement that still counts as a tap
const DECK_SIZE = 5;          // a tasting flight, not the roster (Nina 08-14)

const AgentSwipeDeck = ({
    agents = [],
    capturedIds = [],
    featuredId = null,
    isLoading = false,
    platformIcon = () => '\u{1F535}',
    onCapture = () => {},
    onOpen = () => {},
    onBrowse = () => {},
    onRegister = () => {},
    // 🔌 09-28 agent wave: the end card's main "your AI" door opens the
    // Bring your AI modal (your own AI, as itself); onRegister stays as the
    // "we voice it" link under it.
    onBringAi = () => {},
}) => {
    // Deck = DECK_SIZE cards (Nina 08-14): featured on top, then a GRAB BAG
    // THAT LEANS BEST — weighted random sampling without replacement
    // (Efraimidis-Spiliotis keys: rand^(1/weight), weight = 1 + likes +
    // captures). Loved/caught agents surface more often but every visit
    // deals a fresh hand. Pure-best (sort by weight) or pure-random
    // (weight = 1) are one-line switches if the live feel says otherwise.
    const [order, setOrder] = useState(null);
    const capturedAtBuildRef = useRef(capturedIds);
    capturedAtBuildRef.current = order === null ? capturedIds : capturedAtBuildRef.current;
    useEffect(() => {
        if (order !== null || !agents.length) return;
        const captured = new Set(capturedAtBuildRef.current);
        const rest = agents
            .filter(a => a.id !== featuredId && !captured.has(a.id))
            .map(a => ({ a, k: Math.pow(Math.random(), 1 / (1 + (a.likes_count || 0) + (a.captures_count || 0))) }))
            .sort((x, y) => y.k - x.k)
            .map(x => x.a.id);
        const top = agents.find(a => a.id === featuredId && !captured.has(a.id));
        setOrder((top ? [top.id, ...rest] : rest).slice(0, DECK_SIZE));
    }, [agents, featuredId, order]);

    const [idx, setIdx] = useState(0);
    // drag = live finger offset; fling = committed exit direction (+1 right)
    const [drag, setDrag] = useState(null);
    const [fling, setFling] = useState(null);
    const startRef = useRef(null);
    const flingTimerRef = useRef(null);
    const [hintSeen, setHintSeen] = useState(false);

    const reduceMotion = useMemo(
        () => { try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; } },
        [],
    );

    // The page behind the deck must not scroll under a swipe.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);
    useEffect(() => () => clearTimeout(flingTimerRef.current), []);

    const ids = order || [];
    const byId = useMemo(() => {
        const m = new Map();
        agents.forEach(a => m.set(a.id, a));
        return m;
    }, [agents]);
    const current = ids[idx] ? byId.get(ids[idx]) : null;
    const next = ids[idx + 1] ? byId.get(ids[idx + 1]) : null;

    const commit = (dir) => {
        if (!current || fling) return;
        setHintSeen(true);
        const finish = () => {
            if (dir > 0) onCapture(current);
            setIdx(i => i + 1);
            setFling(null);
            setDrag(null);
        };
        if (reduceMotion) { finish(); return; }
        setFling({ dir });
        flingTimerRef.current = setTimeout(finish, FLING_MS);
    };

    const onPointerDown = (e) => {
        if (fling) return;
        startRef.current = { x: e.clientX, y: e.clientY };
        e.currentTarget.setPointerCapture?.(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!startRef.current || fling) return;
        setDrag({ dx: e.clientX - startRef.current.x, dy: e.clientY - startRef.current.y });
    };
    const onPointerUp = () => {
        if (!startRef.current || fling) return;
        const d = drag || { dx: 0, dy: 0 };
        startRef.current = null;
        if (Math.abs(d.dx) >= SWIPE_THRESHOLD) { commit(d.dx > 0 ? 1 : -1); return; }
        // A clean tap fires NO pointermove, so drag can be null here — judge
        // by distance only.
        if (Math.abs(d.dx) <= TAP_SLOP && Math.abs(d.dy) <= TAP_SLOP && current) {
            setDrag(null);
            onOpen(current);
            return;
        }
        setDrag(null); // spring back (CSS transition below)
    };

    const dx = fling ? fling.dir * (window.innerWidth + 240) : (drag?.dx || 0);
    const dy = fling ? -40 : (drag?.dy || 0) * 0.25;
    const rot = fling ? fling.dir * 22 : (drag?.dx || 0) * 0.06;
    const stampOpacity = Math.min(1, Math.abs(drag?.dx || 0) / SWIPE_THRESHOLD);
    const dragging = !!drag && !fling;

    const renderCard = (agent, isTop) => (
        <div
            key={agent.id}
            role="group"
            aria-label={agent.name}
            onPointerDown={isTop ? onPointerDown : undefined}
            onPointerMove={isTop ? onPointerMove : undefined}
            onPointerUp={isTop ? onPointerUp : undefined}
            onPointerCancel={isTop ? onPointerUp : undefined}
            style={{
                ...styles.card,
                ...(isTop ? {
                    transform: `translate(${dx}px, ${dy}px) rotate(${rot}deg)`,
                    transition: dragging ? 'none' : `transform ${fling ? FLING_MS : 250}ms ease`,
                    touchAction: 'none',
                    cursor: 'grab',
                    zIndex: 2,
                } : {
                    transform: 'scale(0.95) translateY(10px)',
                    zIndex: 1,
                }),
            }}
        >
            <div style={styles.cardArt}>
                {agent.avatar_url
                    ? <img src={agent.avatar_url} alt="" draggable={false} style={styles.cardArtImg} />
                    : <span style={styles.cardArtFallback}>&#129302;</span>}
                {isTop && (
                    <>
                        <div style={{ ...styles.stamp, ...styles.stampCatch, opacity: (drag?.dx || 0) > 0 ? stampOpacity : (fling?.dir === 1 ? 1 : 0) }}>
                            CAUGHT &#127919;
                        </div>
                        <div style={{ ...styles.stamp, ...styles.stampPass, opacity: (drag?.dx || 0) < 0 ? stampOpacity : (fling?.dir === -1 ? 1 : 0) }}>
                            PASS
                        </div>
                    </>
                )}
            </div>
            <div style={styles.cardBody}>
                <div style={styles.cardName}>
                    {agent.name}
                    <span style={styles.cardPlatform}>{platformIcon(agent.source_platform)} {agent.source_platform || 'custom'}</span>
                </div>
                <div style={styles.cardTags}>
                    {(agent.tags || []).slice(0, 3).map(t => <span key={t} style={styles.cardTag}>{t}</span>)}
                </div>
                <div style={styles.cardDesc}>{agent.description || 'A mystery. Swipe and find out.'}</div>
                <div style={styles.cardStats}>
                    &#127919; {agent.captures_count || 0} saved &nbsp;&middot;&nbsp; &#128077; {agent.likes_count || 0}
                </div>
            </div>
        </div>
    );

    const done = order !== null && idx >= ids.length;

    return (
        <div style={styles.overlay}>
            {/* ✕ sits LEFT (Nina 08-14): the LofiRadio music FAB is global
                chrome fixed at top-right with z-index 100010, so it painted
                straight over a close button in that corner. The bar reserves
                the right inset for it rather than fighting it — the FAB stays
                where users expect it on every other page, and the deck stops
                putting anything under it. */}
            <div style={styles.topBar}>
                <button onClick={onBrowse} aria-label="Close and browse the marketplace" style={styles.closeX}>&#10005;</button>
                <span style={styles.eyebrow}>&#10024; AI MARKETPLACE</span>
                {order !== null && !done && <span style={styles.counter}>{Math.min(idx + 1, ids.length)} / {ids.length}</span>}
            </div>

            {order === null && (
                <div style={styles.centerNote}>{isLoading ? 'Shuffling the deck…' : 'No agents yet.'}</div>
            )}

            {order !== null && !done && current && (
                <>
                    <div style={styles.stack}>
                        {next && renderCard(next, false)}
                        {renderCard(current, true)}
                    </div>
                    <div style={styles.actionRow}>
                        <button onClick={() => commit(-1)} aria-label="Pass" style={{ ...styles.actionBtn, ...styles.passBtn }}>&#10006;</button>
                        <button onClick={() => commit(1)} aria-label="Save" style={{ ...styles.actionBtn, ...styles.catchBtn }}>&#127919;</button>
                    </div>
                    {!hintSeen && <div style={styles.hint}>Swipe right to catch. Left to pass. Tap to meet.</div>}
                </>
            )}

            {done && (
                <div style={styles.endCard}>
                    <div style={styles.endEmoji}>&#127881;</div>
                    <div style={styles.endTitle}>{ids.length ? "That's the deck." : 'You saved them all.'}</div>
                    <div style={styles.endSub}>New deal every visit. The full roster is one tap away.</div>
                    <button onClick={onBrowse} style={styles.endPrimary}>Browse the marketplace</button>
                    {/* 🔌 09-28 agent wave: was onRegister (/agents/import, the
                        "we voice it" wizard), the wrong door (Nina). */}
                    <button onClick={onBringAi} style={styles.endSecondary}>&#129302; Bring your AI in</button>
                    <button onClick={onRegister} style={styles.endLink}>Or make one here, and we voice it &rarr;</button>
                </div>
            )}
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed', inset: 0, zIndex: 2000,
        background: 'linear-gradient(135deg, #1a1a2e 0%, #2d1b69 45%, #16213e 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        overflow: 'hidden', color: '#fff',
        paddingTop: 'env(safe-area-inset-top, 0px)',
    },
    topBar: {
        width: '100%', display: 'flex', alignItems: 'center', gap: 10,
        // 56px right inset = the music FAB's lane (34px button + 10px inset +
        // breathing room). Nothing in this bar may enter it.
        padding: '14px 56px 6px 16px',
    },
    eyebrow: { fontSize: '0.68rem', fontWeight: 700, letterSpacing: '1.5px', color: '#c4b5fd' },
    // Visible pill on purpose (Nina 08-14: people must SEE the deck is
    // finite) — the first cut was 0.72rem at 55% white and read as absent.
    counter: {
        fontSize: '0.85rem', fontWeight: 800, color: '#fff',
        background: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: 999, padding: '3px 12px', flexShrink: 0,
        // Parks at the inner edge of the FAB lane, so the row reads
        // ✕ · AI MARKETPLACE ......... 2/5 · [music FAB]
        marginLeft: 'auto',
    },
    // ✕ = close-to-homepage (Nina 08-14; replaced the "Browse all" pill —
    // the end card keeps the explicit Browse button).
    //
    // ⭐ Made VISIBLE 08-14 pm (Nina: "let's make the X more visible I guess").
    // Same lesson the counter pill above already learned in this file: a
    // translucent control on a dark overlay reads as decoration, not an
    // affordance. Translucency was the whole problem, so this INVERTS instead
    // of tinting — a near-solid light disc with a dark glyph is the one
    // treatment that cannot disappear into the artwork behind it, and the
    // cards it sits over are dark (#14121f) and busy.
    //
    // 44px = the iOS minimum tap target. The old 34px was under it, so on a
    // phone this was hard to HIT as well as hard to see.
    //
    // POSITION: top-LEFT (moved 08-14 pm, Nina: "the music player covers the
    // x button during swiping on my mobile"). It WAS top-right — the standard
    // dismiss corner — but that corner is not ours: LofiRadio's music FAB is
    // global chrome, `position: fixed` at top-right with zIndex 100010, so it
    // paints over this whole overlay (z 2000) no matter how visible the disc
    // is. Making the button prettier could never have fixed it.
    // Left was chosen over moving the FAB because the FAB is shared by five
    // pages and users expect it in one place; the deck is the newcomer here.
    // Still clear of the two thumb-zone action buttons at the bottom — a
    // destructive-feeling exit does not want to sit next to the controls
    // people tap fastest.
    //
    // The GLYPH stays a bare ✕ on purpose: Nina removed the "Browse all" pill
    // earlier the same day. This is a visibility fix, not a walk-back of that.
    // If the word should come back, it is one label here.
    closeX: {
        width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
        background: 'rgba(255,255,255,0.92)', color: '#14121f', border: 'none',
        fontSize: '1.2rem', fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit',
        lineHeight: 1, padding: 0,
        boxShadow: '0 2px 10px rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    stack: {
        position: 'relative', flex: 1, width: 'min(430px, calc(100vw - 28px))',
        margin: '6px auto 0', minHeight: 0,
    },
    card: {
        position: 'absolute', inset: '4px 0 8px', display: 'flex', flexDirection: 'column',
        background: '#14121f', borderRadius: 18, overflow: 'hidden',
        border: '1px solid rgba(196,181,253,0.28)',
        boxShadow: '0 14px 44px rgba(0,0,0,0.45)',
        userSelect: 'none', WebkitUserSelect: 'none',
    },
    cardArt: {
        position: 'relative', flex: '1 1 58%', minHeight: 0,
        background: 'radial-gradient(circle at 50% 30%, #3b2f63, #191627)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    cardArtImg: { width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' },
    cardArtFallback: { fontSize: 84, opacity: 0.85 },
    stamp: {
        position: 'absolute', top: 18, fontSize: '1.5rem', fontWeight: 900, letterSpacing: '0.06em',
        padding: '4px 12px', borderRadius: 8, border: '3px solid', pointerEvents: 'none',
    },
    stampCatch: { left: 14, color: '#4ade80', borderColor: '#4ade80', transform: 'rotate(-14deg)', background: 'rgba(8,20,12,0.55)' },
    stampPass: { right: 14, color: '#f87171', borderColor: '#f87171', transform: 'rotate(14deg)', background: 'rgba(24,8,8,0.55)' },
    cardBody: { padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', gap: 7 },
    cardName: {
        fontSize: '1.25rem', fontWeight: 800, display: 'flex', alignItems: 'baseline', gap: 10,
        flexWrap: 'wrap',
    },
    cardPlatform: { fontSize: '0.7rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)' },
    cardTags: { display: 'flex', gap: 6, flexWrap: 'wrap' },
    cardTag: {
        fontSize: '0.68rem', fontWeight: 600, color: '#c4b5fd',
        background: 'rgba(196,181,253,0.12)', border: '1px solid rgba(196,181,253,0.3)',
        borderRadius: 999, padding: '2px 9px',
    },
    cardDesc: {
        fontSize: '0.85rem', color: 'rgba(255,255,255,0.75)', lineHeight: 1.4,
        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden',
    },
    cardStats: { fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' },
    actionRow: {
        display: 'flex', gap: 26, justifyContent: 'center', alignItems: 'center',
        padding: '12px 0 6px', flexShrink: 0,
    },
    actionBtn: {
        width: 58, height: 58, borderRadius: '50%', fontSize: '1.4rem', cursor: 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'inherit',
        background: 'rgba(255,255,255,0.06)',
    },
    passBtn: { border: '2px solid rgba(248,113,113,0.7)', color: '#f87171' },
    catchBtn: { border: '2px solid rgba(74,222,128,0.7)', color: '#4ade80' },
    hint: {
        textAlign: 'center', fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)',
        paddingBottom: 'calc(12px + env(safe-area-inset-bottom, 0px))',
    },
    centerNote: { flex: 1, display: 'flex', alignItems: 'center', color: 'rgba(255,255,255,0.7)' },
    endCard: {
        flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        gap: 10, padding: '0 28px calc(24px + env(safe-area-inset-bottom, 0px))', textAlign: 'center',
    },
    endEmoji: { fontSize: 56 },
    endTitle: { fontSize: '1.5rem', fontWeight: 800 },
    endSub: { fontSize: '0.9rem', color: 'rgba(255,255,255,0.65)', marginBottom: 10 },
    endPrimary: {
        background: '#7C4DFF', color: '#fff', border: 'none', borderRadius: 12,
        padding: '13px 26px', fontSize: '1rem', fontWeight: 800, cursor: 'pointer',
        fontFamily: 'inherit', width: 'min(320px, 100%)',
    },
    endSecondary: {
        background: 'rgba(255,255,255,0.08)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: 12, padding: '11px 26px', fontSize: '0.9rem', fontWeight: 700,
        cursor: 'pointer', fontFamily: 'inherit', width: 'min(320px, 100%)',
    },
    // 🔌 09-28 agent wave: the quiet "we voice it" link under the main door.
    endLink: {
        background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)',
        textDecoration: 'underline', fontSize: '0.82rem', fontFamily: 'inherit',
        padding: '6px 10px', cursor: 'pointer',
    },
};

export default AgentSwipeDeck;
